import { FormEvent, Fragment } from "react";
import { useTranslation } from "react-i18next";
import "@static/img/eircode/EirCodeLogo-blue.png";
import "@static/img/eircode/EirCodeLogo-pink.png";
import "./css/EirCode.scss";
import { useEirCodeFinder } from "./hook/useEirCodeFinder";
import { countyFormatter } from "./utils/countyFormatter";
import { UseFormMethods, useWatch } from "react-hook-form";
import { FieldErrors } from "react-hook-form/dist/types/errors";
import { IUserAddress } from "@common/Eircode/interface/IAddressForm";
import { alphaNumericRegex } from "itsf-ui-common";
import { Loader } from "@common/Loader/Loader";
import { Button, Grid, MenuItem, Select, TextField, Typography } from "@mui/material";

interface IParams<TFieldValues> {
    addressForm: UseFormMethods<TFieldValues> & Partial<UseFormMethods>;
    buttonColor: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
    eirCodeLogoColor: "blue" | "pink";
    isEditing: boolean;
    isUpdated: boolean;
    prefix?: string;
}

export const EirCode = <TFieldValues extends unknown>(props: IParams<TFieldValues>) => {
    const { t } = useTranslation(["address", "common", "validation"]);
    const { eirCodeLogoColor, isEditing, addressForm, isUpdated, buttonColor, prefix } = props;
    const { errors, register } = addressForm;

    const formErrors = (prefix ? errors[prefix] : errors) as FieldErrors<IUserAddress>;
    const namePrefix = prefix ? `${prefix}.` : "";
    const idPrefix = prefix ? `${prefix}-` : "";

    const eirCodeLogoPath = `/img/EirCodeLogo-${eirCodeLogoColor}.png`;

    const {
        submitEircode,
        eircodeAddressList,
        getEircodeAddressListState,
        eircodeSelectedAddress,
        selectAddress,
        onChangeEircode,
        errorMessage,
    } = useEirCodeFinder(addressForm, prefix);

    const isFieldDisabled = !isEditing || isUpdated;
    const eirCodeValue = useWatch<string>({
        name: `${namePrefix}code`,
        control: addressForm.control,
        defaultValue: "",
    });
    const eirCodeButtonIsDisabled = !eirCodeValue || formErrors?.code !== undefined || isUpdated;

    const findAddress = (event: FormEvent) => {
        event.preventDefault();
        submitEircode();
    };

    const hasCodeValue = useWatch<string>({ name: `${namePrefix}code`, control: addressForm.control });

    return (
        <Fragment>
            <Grid item md={5} xs={12}>
                <TextField
                    InputLabelProps={{ shrink: Boolean(hasCodeValue) }}
                    disabled={isFieldDisabled || getEircodeAddressListState.isLoading}
                    error={Boolean(formErrors?.code)}
                    fullWidth
                    helperText={formErrors?.code?.message}
                    id={`${idPrefix}eircodeText`}
                    inputRef={register({
                        pattern: { value: alphaNumericRegex, message: t("validation:onlyLettersNumbers") },
                    })}
                    label={t("address:eircode")}
                    margin="dense"
                    name={`${namePrefix}code`}
                    type="text"
                    variant="standard"
                    onChange={(event) => onChangeEircode(event.target.value)}
                />
            </Grid>
            {eircodeSelectedAddress !== undefined && (
                <Grid item md={5} xs={12}>
                    <Typography color="secondary" variant="small">
                        {t("address:pleaseSelectAddress")}:
                    </Typography>
                    <Select
                        disabled={isFieldDisabled || getEircodeAddressListState.isLoading}
                        fullWidth
                        id={`${idPrefix}eircodeSelect`}
                        name={`${namePrefix}eircode`}
                        value={eircodeSelectedAddress}
                        variant="outlined"
                        onChange={(event) => selectAddress(parseInt(event.target.value as string))}
                    >
                        {eircodeAddressList?.map((address, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <MenuItem key={index} value={index}>
                                {address.addressLine1} - {address.town} - {countyFormatter(address.county)}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            )}
            {errorMessage && (
                <Grid container>
                    <Typography color="error" variant="small">
                        {errorMessage}
                    </Typography>
                </Grid>
            )}
            <Grid className="eircode" container>
                <Grid className="eircode__buttons" item md={5} xs={12}>
                    {isEditing && !getEircodeAddressListState.isLoading && (
                        <Fragment>
                            <Button
                                color={buttonColor}
                                href="https://finder.eircode.ie/#/"
                                id={`${idPrefix}FindEirCodeButton`}
                                rel="noreferrer"
                                size="large"
                                target="_blank"
                                variant="outlined"
                            >
                                {t("address:findWith")}
                                <img alt="Eir code finder" className="eircode__logo" src={eirCodeLogoPath} />
                            </Button>
                            <Button
                                color={buttonColor}
                                disabled={eirCodeButtonIsDisabled}
                                id={`${idPrefix}eircodeConfirmButton`}
                                type="submit"
                                variant="contained"
                                onClick={findAddress}
                            >
                                {t("common:confirm")} {t("address:eircode")}
                            </Button>
                        </Fragment>
                    )}
                    {getEircodeAddressListState.isLoading && <Loader size={40} />}
                </Grid>
            </Grid>
        </Fragment>
    );
};
